import { View } from "react-native";
import ActivityButton from "./ActivityButton";
import { useNavigation } from '@react-navigation/native';
import { Head } from "@shared/components/head";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { ActivityCompleteCard } from "./ActivityCompletedCard";
import { AppointmentCard } from "./AppointmentCard";
import { ActivityType, IPathwayActivitiesResponse, IPathwayActivity } from "app/_modules/pathways/entities";
import { isActivityTimeValid } from "@shared/utils";
import { ACTIVITY_STATUS } from "../../constants";
import { mapToCardStatus, mapModalityToType } from "../../utils/appointment";
import { ActivityName } from "../../types";
import { Telemetry } from "app/services/telemetry/Telemetry";
import { APPOINTMENT_TYPE } from '../../constants/appointmentModality';

interface ActivitiesSectionProps {
    activities: IPathwayActivitiesResponse;
    showDayCategory?: boolean;
}

const ActivitiesSection = ({ activities, showDayCategory = true }: ActivitiesSectionProps) => {
    const { t } = dynamicTranslations();
    const navigation = useNavigation<any>();

    const handleActivityNavigation = (activityName: string, activityId: string, activityType: string, doctor?: any) => {
        Telemetry.event('ui.home.activity_started', {
            domain: 'ui',
            data: {
                activityName,
                activityId,
                activityType
            }
        });

        switch (activityType) {
            case ActivityType.PATHWAY_VITAL_PARAMETER:
                navigation.navigate('MeasurementFlow', { activityName, activityId });
                break;
            case ActivityType.PATHWAY_DRUG:
                navigation.navigate('DrugIntakeFlow', { activityName, activityId });
                break;
            case ActivityType.PATHWAY_QUESTIONNAIRE:
                navigation.navigate('QuestionnaireFlow', { activityName, activityId });
                break;
            case ActivityType.PATHWAY_APPOINTMENT:
                navigation.navigate('AppointmentScreen', { activityName, appointmentId: activityId, doctor });
                break;
        }
    }

    const fullDayActivities = activities.daily || [];
    const timeSpecificActivities: { [key: string]: IPathwayActivity[] } = {
        morning: activities.morning || [],
        afternoon: activities.afternoon || [],
        evening: activities.evening || []
    };

    Object.keys(timeSpecificActivities).forEach((key) => {
        timeSpecificActivities[key].sort((a, b) => {
            if (a.activityType === ActivityType.PATHWAY_APPOINTMENT && b.activityType !== ActivityType.PATHWAY_APPOINTMENT) {
                return -1;
            }
            if (a.activityType !== ActivityType.PATHWAY_APPOINTMENT && b.activityType === ActivityType.PATHWAY_APPOINTMENT) {
                return 1;
            }
            if (a.activityType === ActivityType.PATHWAY_APPOINTMENT && b.activityType === ActivityType.PATHWAY_APPOINTMENT) {
                const dateA = a.appointment?.date || '';
                const dateB = b.appointment?.date || '';
                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }
                const timeA = a.appointment?.time || '';
                const timeB = b.appointment?.time || '';
                return timeA.localeCompare(timeB);
            }
            return 0;
        });
    });

    return (
        <View className={`flex-col ${showDayCategory ? 'gap-4' : 'gap-2'}`}>
            {fullDayActivities.length > 0 && (
                <View className="flex-col gap-2">
                    {fullDayActivities.map((activity: IPathwayActivity) => {
                        if (activity.status === ACTIVITY_STATUS.COMPLETED || activity.activityName === ActivityName.STEP) {
                            return <ActivityCompleteCard key={activity.activityId} activity={activity} />;
                        }
                        return (
                            <ActivityButton
                                key={activity.activityId}
                                activityName={activity.activityName}
                                activityType={activity.activityType}
                                onPress={() => handleActivityNavigation(activity.activityName, activity.activityId, activity.activityType)}
                                disabled={activity.activityType === ActivityType.PATHWAY_EXAM}
                            />
                        );
                    })}
                </View>
            )}

            {/* TECHDEBT
            Issue: https://app.clickup.com/t/86c726u7a
            Description: Refactor time-based activity rendering: extract mapper and simplify UI/status logic
             */}

            {Object.entries(timeSpecificActivities).filter(([, value]) => value.length > 0).map(([key, value]) => {
                return (
                    <View key={key} className="flex-col gap-2">
                        {showDayCategory && <Head type="h3">{t(`commons.timeofday.${key}`)}</Head>}
                        {value.map((activity: IPathwayActivity) => {
                            const isTimeValid = isActivityTimeValid(key);

                            if (activity.activityType === ActivityType.PATHWAY_APPOINTMENT) {
                                if (activity.activityName === 'televisit' && activity.appointment) {
                                    const cardStatus = mapToCardStatus(activity.appointment.status, isTimeValid);
                                    return (
                                        <AppointmentCard
                                            key={activity.activityId}
                                            type="televisit"
                                            status={cardStatus}
                                            time={activity.appointment.time}
                                            date={activity.appointment.date}
                                            professional={activity.appointment.professional}
                                            reason={activity.appointment.reason}
                                            onConnect={cardStatus === 'upcoming'
                                                ? () => navigation.navigate('AppointmentScreen', {
                                                    appointmentId: activity.appointment!.id,
                                                    activityId: activity.activityId,
                                                    scheduledTime: activity.appointment!.time?.substring(0, 5),
                                                    professional: activity.appointment!.professional,
                                                    appointmentType: APPOINTMENT_TYPE.TELEVISIT,
                                                })
                                                : undefined}
                                        />
                                    );
                                }

                                if (activity.appointment) {
                                    const apptType = mapModalityToType(activity.appointment.modality || '');
                                    const apptStatus = mapToCardStatus(activity.appointment.status, isTimeValid);
                                    return (
                                        <AppointmentCard
                                            key={activity.activityId}
                                            type={apptType}
                                            status={apptStatus}
                                            time={activity.appointment.time}
                                            date={activity.appointment.date}
                                            activityName={activity.appointment.exam?.label ?? activity.activityName}
                                            professional={activity.appointment.professional}
                                            onConnect={apptType === 'teleassistance' && apptStatus === 'upcoming'
                                                ? () => navigation.navigate('AppointmentScreen', {
                                                    appointmentId: activity.appointment!.id,
                                                    activityId: activity.activityId,
                                                    scheduledTime: activity.appointment!.time?.substring(0, 5),
                                                    professional: activity.appointment!.professional,
                                                    appointmentType: APPOINTMENT_TYPE.TELEASSISTANCE,
                                                })
                                                : undefined}
                                        />
                                    );
                                }
                            }

                            if (activity.status === ACTIVITY_STATUS.COMPLETED) {
                                return <ActivityCompleteCard key={activity.activityId} activity={activity} />;
                            }
                            return (
                                <ActivityButton
                                    key={activity.activityId}
                                    activityName={activity.activityName}
                                    activityType={activity.activityType}
                                    onPress={() => handleActivityNavigation(activity.activityName, activity.activityId, activity.activityType)}
                                    disabled={!isTimeValid || activity.activityType === ActivityType.PATHWAY_EXAM}
                                />
                            );
                        })}
                    </View>
                )
            })}
        </View>
    );
}

export default ActivitiesSection;
